import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import SkillPickerModal from './SkillPickerModal';
import RoutineRuleCheck from './RoutineRuleCheck';
import { totalDifficulty, markRepeats, difficultyFor } from '../../utils/routineDifficulty';
import { APPARATUS } from './routineConstants';
import './routineRepeat.css';
import './GymnastRoutines.css';

// Usual number of skills per routine. A soft hint only: coaches often build
// part-routines while training, so a mismatch never blocks saving.
const USUAL_LENGTH = { TRA: 10, DMT: 2, TUM: 8 };
export const MAX_ROUTINE_SKILLS = 20;

let nextKey = 0;
export const withKey = (row) => ({ ...row, key: `row-${nextKey++}` });

// Shaped API skills -> editable rows
export const toRows = (skills) => skills.map(s => withKey({
  skillId: s.isImplicit ? null : s.skillId,
  name: s.name,
  figNotation: s.figNotation,
  difficulty: s.difficulty,
  dmtDifficulty: s.dmtDifficulty,
  isImplicit: s.isImplicit,
}));

// Editable rows -> API skill rows
export const toPayloadSkills = (rows) => rows.map(r => (r.isImplicit
  ? {
    customSkillName: r.name,
    difficulty: r.difficulty == null || r.difficulty === '' ? null : Number(r.difficulty),
    figNotation: r.figNotation || null,
  }
  : { skillId: r.skillId }));

// The skills part of a routine form: the ordered list with running difficulty,
// repeat warnings, move/remove, and + Add skill. Used for gymnast routines and
// level routines. rows/onChange hold the list; the parent saves it.
export default function RoutineSkillsEditor({ rows, onChange, apparatus, availableSkills, gymnastId = null }) {
  // Prep skills (Forward Roll and the like) can't go in a routine
  const routineSkills = useMemo(() => availableSkills.filter(s => !s.prepSkill), [availableSkills]);
  const [showPicker, setShowPicker] = useState(false);

  const addSkill = (skillId) => {
    const skill = availableSkills.find(s => s.id === skillId);
    if (!skill) return;
    onChange([...rows, withKey({
      skillId, name: skill.name, figNotation: skill.figNotation,
      difficulty: skill.difficulty, dmtDifficulty: skill.dmtDifficulty, isImplicit: false,
    })]);
  };

  const moveRow = (index, delta) => {
    const next = rows.slice();
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    onChange(next);
  };

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  const usual = USUAL_LENGTH[apparatus];
  const apparatusLabel = APPARATUS.find(a => a.value === apparatus)?.label;
  const runningTotal = totalDifficulty(rows, apparatus);

  return (
    <div className="form-group">
      <div className="gymnast-routine-editor__skills-header">
        <label>Skills</label>
        <span>Total DD {runningTotal.toFixed(1)}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted">No skills yet.</p>
      ) : (
        <ol className="gymnast-routine-editor__skills">
          {markRepeats(rows).map((row, i) => { const rowDD = difficultyFor(row, apparatus); return (
            <li key={row.key} className={[row.isImplicit && 'is-implicit', row.isRepeat && 'is-repeat'].filter(Boolean).join(' ') || undefined}>
              <div className="gymnast-routine-editor__skill-text">
                <span className="gymnast-routine-editor__skill-name">
                  {row.name}
                  {row.isImplicit && <span className="routine-legacy-tag" title="Typed in before routines used the skills library. A club admin can swap it for a library skill.">Not in library</span>}
                  {row.isRepeat && <span className="routine-repeat-tag" title="Repeated move: scores no difficulty">Repeat</span>}
                </span>
                {(row.figNotation || rowDD != null) && (
                  <span className="gymnast-routine__skill-meta">
                    {row.figNotation && <code>{row.figNotation}</code>}
                    {rowDD != null && <span>{row.isRepeat ? '0.0' : rowDD.toFixed(1)}</span>}
                  </span>
                )}
              </div>
              <div className="gymnast-routine-editor__skill-actions">
                <button type="button" className="edit-mode-btn" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label={`Move ${row.name} up`}>▲</button>
                <button type="button" className="edit-mode-btn" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} aria-label={`Move ${row.name} down`}>▼</button>
                <button type="button" className="edit-mode-btn edit-mode-btn--delete" onClick={() => removeRow(i)} aria-label={`Remove ${row.name}`}>×</button>
              </div>
            </li>
          );})}
        </ol>
      )}
      {rows.length > 0 && usual && rows.length !== usual && (
        <p className="gymnast-routine-editor__hint">
          {apparatusLabel} routines usually have {usual} skills; this one has {rows.length}.
        </p>
      )}
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={() => setShowPicker(true)}
        disabled={rows.length >= MAX_ROUTINE_SKILLS}
      >
        + Add skill
      </button>
      <RoutineRuleCheck gymnastId={gymnastId} apparatus={apparatus} rows={rows} />

      {/* Outside the parent's <form>, so Enter in the picker can't submit it */}
      {showPicker && createPortal(
        <SkillPickerModal
          availableSkills={routineSkills}
          apparatus={apparatus}
          onSave={addSkill}
          onCancel={() => setShowPicker(false)}
        />,
        document.body,
      )}
    </div>
  );
}
